import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ApiService } from './api.service'; // Import ApiService

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(
    private toastr: ToastrService,
    private router: Router,
    private apiService: ApiService, // Add ApiService
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        let errorMessage = 'An unexpected error occurred';

        if (error.status === 0) {
          errorMessage = 'Unable to connect to the server. Please check your connection or try again later.';
          if (error.error instanceof Error && error.error.message.includes('UNABLE_TO_VERIFY_LEAF_SIGNATURE')) {
            errorMessage = 'SSL certificate error. Please contact support.';
            console.error('SSL Error:', error.error);
          }
        } else if (error.error) {
          if (error.status === 400) {
            errorMessage = error.error.message || error.error.msg || 'Bad request';
          } else if (error.status === 401) {
            const token = this.apiService.getToken && this.apiService.getToken();
            if (!token) {
              errorMessage = 'Unauthorized. Please log in.';
              if (isPlatformBrowser(this.platformId)) {
                this.router.navigate(['/auth/login']);
              }
            } else {
              if (req.headers.get('x-refresh-tried')) {
                errorMessage = 'Session expired. Please log in again.';
                if (isPlatformBrowser(this.platformId)) {
                  this.router.navigate(['/auth/login']);
                }
              } else {
                return new Observable<HttpEvent<any>>((observer) => {
                  import('firebase/auth').then(({ getAuth, onAuthStateChanged }) => {
                    const auth = getAuth();
                    const unsubscribe = onAuthStateChanged(auth, async (user: any) => {
                      unsubscribe();
                      if (!user) {
                        if (isPlatformBrowser(this.platformId)) {
                          this.router.navigate(['/auth/login']);
                        }
                        observer.error(new Error('No authenticated user'));
                        return;
                      }
                      try {
                        const newToken = await user.getIdToken(true);
                        if (isPlatformBrowser(this.platformId)) {
                          localStorage.setItem('GProToken', JSON.stringify(newToken));
                        }
                        const cloned = req.clone({
                          setHeaders: { Authorization: `Bearer ${newToken}`, 'x-refresh-tried': '1' }
                        });
                        next.handle(cloned).subscribe({
                          next: (ev) => observer.next(ev),
                          error: (err) => observer.error(err),
                          complete: () => observer.complete()
                        });
                      } catch (refreshErr) {
                        if (isPlatformBrowser(this.platformId)) {
                          this.router.navigate(['/auth/login']);
                        }
                        observer.error(refreshErr);
                      }
                    });
                  }).catch((impErr) => {
                    console.error('Failed to import firebase/auth for token refresh', impErr);
                    if (isPlatformBrowser(this.platformId)) {
                      this.router.navigate(['/auth/login']);
                    }
                    observer.error(impErr);
                  });
                });
              }
            }
          } else if (error.status === 403) {
            errorMessage = 'Forbidden. You do not have access.';
          } else if (error.status === 404) {
            errorMessage = 'Requested resource not found.';
          } else if (error.status === 422) {
            errorMessage = this.formatValidationErrors(error.error.errors);
          } else if (error.status >= 500) {
            errorMessage = 'Internal server error. Please try again later.';
          }
        }

        if (isPlatformBrowser(this.platformId)) {
          this.toastr.error(errorMessage, 'Error');
        } else {
          console.error('SSR Error:', errorMessage, error);
        }

        return throwError(() => new Error(errorMessage));
      })
    );
  }

  private formatValidationErrors(errors: any): string {
    if (!errors || typeof errors !== 'object') {
      console.warn('Invalid validation errors:', errors);
      return 'Validation error occurred';
    }
    try {
      return Object.values(errors).flat().join(' ');
    } catch (err) {
      console.error('Error formatting validation errors:', err, errors);
      return 'Invalid input data';
    }
  }
}